import { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  useQuery,
  gql,
  ApolloError,
  OperationVariables,
  ApolloQueryResult,
} from '@apollo/client';
import { useApolloContext } from '..';
import { config } from '../../config';
import ky from 'ky-universal';
import { Landing } from '../../tabs';
import { Loader } from '@fluentui/react-northstar';
import { USER_AZURE_ID } from '../../constants/location';
import { getAccessToken } from '../../services/teamsBot';
import {
  UserContextQueryQuery,
  UserContextQueryUser,
} from './__generated__/UserContext.generated';
import { useTenantLocalStorage } from '../../utilities';
import { getTokenTenantId, resolveTenant } from '../../utilities/TokenTenant';
import { useAmplitudeLogger } from '../AmplitudeLoggerContext';
import { logError, setSentryUser } from '../../utilities/ErrorLogger';

export type ApiOrganizations = {
  data: OrganizationData[];
};

export type OrganizationData = {
  id: string;
  is_organization: boolean;
  name: string;
  slug: string;
  uuid?: string;
  avatar: string;
};

const GET_CURRENT_USER_QUERY = gql`
  query UserContextQuery {
    getCurrentUserAndOrgs {
      userId
      user {
        id
        name
        timeZone
        defaultLocation
        avatar
      }
    }
  }
`;

type UserContextValue = {
  loading: boolean;
  error: undefined | ApolloError;
  currentUser: null | UserContextQueryUser;
  refetch?: (
    variables?: Partial<OperationVariables> | undefined
  ) => Promise<ApolloQueryResult<UserContextQueryQuery>>;
  currentOrgs: OrganizationData[] | undefined;
  azureAdId: string | undefined;
};

const UserContext = createContext<UserContextValue>({
  loading: true,
  error: undefined,
  currentUser: null,
  refetch: undefined,
  currentOrgs: undefined,
  azureAdId: undefined,
});

export const UserContextProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const [userOrganizations, setUserOrganizations] = useState<
    OrganizationData[] | undefined
  >(undefined);
  const { tenantId, jwt, setTenantId, setJwt } = useApolloContext();
  const [azureAdId, setAzureAdId] = useTenantLocalStorage<string>(
    USER_AZURE_ID,
    undefined
  );
  const [teamsContextloading, setTeamsContextLoading] = useState<boolean>(true);
  const lastReportedGqlErrorRef = useRef<string | null>(null);
  const [userContextHasError, setUserContextHasError] =
    useState<boolean>(false);

  const { teamsBotServiceUrl } = config;
  const serviceUrl = `${teamsBotServiceUrl}/token`;

  const { setUserId, setOrgInfo, reportEvent } = useAmplitudeLogger();

  useEffect(() => {
    if (!serviceUrl) {
      return;
    }

    try {
      const testKey = '__robin_ls_test__';
      localStorage.setItem(testKey, '1');
      localStorage.removeItem(testKey);
    } catch {
      console.error('UserContext: localStorage is not available', {
        tenantId,
      });
      logError(new Error('UserContext: localStorage is not available'));
    }

    let simulatedLoadingTimeout: NodeJS.Timeout;

    if (process.env.REACT_APP_IS_OUTSIDE_TEAMS === 'true') {
      simulatedLoadingTimeout = setTimeout(async () => {
        setAzureAdId(process.env.AZURE_ACTIVE_DIR_ID);
        setJwt(process.env.REACT_APP_LOCAL_JWT);

        const { robinApiUrl } = config;
        const api = ky.extend({
          hooks: {
            beforeRequest: [
              (request) => {
                request.headers.set(
                  'Authorization',
                  `Bearer ${process.env.REACT_APP_LOCAL_JWT}`
                );
              },
            ],
          },
        });

        const orgs: ApiOrganizations = await api
          .get(`${robinApiUrl}/v1.0/me/organizations?per_page=50`)
          .json();

        setUserOrganizations(orgs.data);

        const selection = resolveTenant({
          tokenTenantId: getTokenTenantId(process.env.REACT_APP_LOCAL_JWT),
          persistedTenantId: tenantId,
          orgs: orgs.data,
        });

        if (selection.tenantId !== tenantId) setTenantId(selection.tenantId);

        setOrgInfo(selection.tenantId, selection.slug);

        setTeamsContextLoading(false);
      }, 1000);

      return;
    }

    const fetchTeamsData = async () => {
      const { robinApiUrl } = config;

      const { app } = await import('@microsoft/teams-js');

      try {
        await app.initialize();
        const context = await app.getContext();
        
        if (!jwt) {
          let userObjectId: string | undefined;
          try {
            userObjectId = context.user?.id;
            if (!userObjectId) {
              throw new Error(
                'User object ID is undefined on the teams context'
              );
            }

            setAzureAdId(userObjectId);
            setSentryUser({ azureAdId: userObjectId });
            const res = await getAccessToken(userObjectId);

            setUserId(userObjectId);

            const bearer: string = res.token.access_token;

            const api = ky.extend({
              hooks: {
                beforeRequest: [
                  (request) => {
                    request.headers.set('Authorization', `Bearer ${bearer}`);
                  },
                ],
              },
            });

            const orgs: ApiOrganizations = await api
              .get(`${robinApiUrl}/v1.0/me/organizations?per_page=50`)
              .json();

            setUserOrganizations(orgs.data);

            // The API rejects a Tenant-Id that differs from the org the token
            // is bound to, so the token's org wins over the persisted tenant
            // and the org list order.
            const selection = resolveTenant({
              tokenTenantId: getTokenTenantId(bearer),
              persistedTenantId: tenantId,
              orgs: orgs.data,
            });

            if (selection.tenantId !== tenantId)
              setTenantId(selection.tenantId);

            setOrgInfo(selection.tenantId, selection.slug);

            setJwt(bearer);
            setTeamsContextLoading(false);
          } catch (e) {
            const err = e instanceof Error ? e : new Error(String(e));
            const httpStatus = (e as { response?: { status?: number } })?.response?.status;
            console.error('UserContext: failed to initialize user session', {
              userObjectId,
              tenantId,
              httpStatus,
            });
            logError(err);
            setUserContextHasError(true);
            setTeamsContextLoading(false);
          }
        }
      } catch (e) {
        console.error('UserContext: failed to initialize Teams SDK', e);
        logError(
          e instanceof Error
            ? e
            : new Error(`UserContext Teams SDK init failed: ${e}`)
        );
        setUserContextHasError(true);
        setTeamsContextLoading(false);
      }
    };

    fetchTeamsData();

    return () => {
      clearTimeout(simulatedLoadingTimeout);
    };
  }, [
    serviceUrl,
    tenantId,
    jwt,
    setTenantId,
    setJwt,
    setAzureAdId,
    setUserId,
    setOrgInfo,
  ]);

  const {
    data,
    loading: userLoading,
    error: userLoadingError,
    refetch,
  } = useQuery<UserContextQueryQuery>(GET_CURRENT_USER_QUERY, {
    skip: !jwt,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (userLoadingError) {
      if (lastReportedGqlErrorRef.current !== userLoadingError.message) {
        lastReportedGqlErrorRef.current = userLoadingError.message;
        console.error('UserContext: GraphQL user query failed', {
          error: userLoadingError,
          tenantId,
        });
        logError(userLoadingError);
      }
    } else {
      lastReportedGqlErrorRef.current = null;
    }
  }, [userLoadingError, tenantId]);

  const loading =
    teamsContextloading ||
    userLoading ||
    (!userLoading && !tenantId && !userContextHasError);

  const currentUser = data?.getCurrentUserAndOrgs?.user;

  const value: UserContextValue = {
    loading,
    error: userLoadingError,
    currentUser: currentUser ?? null,
    refetch,
    currentOrgs: userOrganizations,
    azureAdId,
  };

  // Report session created whenever a new user context is initialized
  if (currentUser?.id) setUserId(currentUser.id);

  const robinUserId = data?.getCurrentUserAndOrgs?.userId;

  useEffect(() => {
    if (!loading && currentUser?.id) {
      setSentryUser({ azureAdId, robinUserId, tenantId });
      reportEvent('session-created');
    }

    if (!loading && !currentUser) {
      console.error('UserContext: rendering Landing — user not resolved', {
        hasJwt: !!jwt,
        tenantId,
        azureAdId,
        hasError: !!userLoadingError,
        hasData: !!data,
        userContextHasError,
      });
      if (jwt && !userLoadingError) {
        logError(
          new Error(
            `UserContext: JWT present but currentUser is null (tenantId: ${tenantId})`
          )
        );
      } else if (!jwt && !userContextHasError) {
        logError(
          new Error(
            `UserContext: no JWT after loading completed (azureAdId: ${azureAdId}, tenantId: ${tenantId})`
          )
        );
      }
    }
  }, [loading, currentUser, reportEvent, jwt, tenantId, azureAdId, robinUserId, userLoadingError, data, userContextHasError]);

  return (
    <UserContext.Provider value={value}>
      {loading ? (
        <Loader></Loader>
      ) : !currentUser ? (
        <Landing></Landing>
      ) : (
        // Only load children (the remaining contexts and the actual app) after the user has been resolved
        !loading && currentUser && children
      )}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextValue => {
  return useContext(UserContext);
};
